import { useEffect, type ReactNode } from 'react';
import { ApplicationFormSharePointPanel } from '../components/forms/settings/ApplicationFormSharePointPanel';
import { CustomerFeedbackSharePointPanel } from '../components/forms/settings/CustomerFeedbackSharePointPanel';
import { SharePointEnquiryPanel } from '../components/settings/SharePointEnquiryPanel';
import { useAdminHubContext } from '../contexts/AdminHubContext';
import {
  canManageCrmConfigInAdminHub,
  canManageFormsConfigInAdminHub,
  canManageItConfigInAdminHub,
} from '../lib/adminHubPermissions';
import { setActivePortalKind } from '../lib/supabase';

function SharePointCategory({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section className="unified-users-dept">
      <header className="unified-users-dept__head">
        <h3 className="unified-users-dept__title">{title}</h3>
        <span className="unified-users-dept__count">
          {count} {count === 1 ? 'connection' : 'connections'}
        </span>
      </header>
      <div className="sharepoint-portal-group__list">{children}</div>
    </section>
  );
}

/** Sales CRM + IT + Application Form SharePoint settings for Admin Hub. */
export function SharePointConfigurationPage() {
  const hubCtx = useAdminHubContext();
  const actorRole = hubCtx?.actorRole ?? 'user';
  const canCrm = canManageCrmConfigInAdminHub(actorRole);
  const canIt = canManageItConfigInAdminHub(actorRole);
  const canForms = canManageFormsConfigInAdminHub(actorRole);

  const connectionCount = (canCrm ? 1 : 0) + (canIt ? 1 : 0) + (canForms ? 2 : 0);

  useEffect(() => {
    setActivePortalKind('crm');
  }, []);

  if (!canCrm && !canIt && !canForms) {
    return <p className="settings-empty">You cannot manage SharePoint configuration.</p>;
  }

  return (
    <section className="card settings-panel integration-settings-panel">
      <div className="settings-panel__head unified-users-panel__head">
        <div className="unified-users-panel__title-row">
          <h2 className="settings-panel__title">SharePoint configuration</h2>
          <div className="unified-users-panel__actions">
            <span className="settings-panel__count">
              {connectionCount} {connectionCount === 1 ? 'connection' : 'connections'}
            </span>
          </div>
        </div>
      </div>

      <div className="unified-users-dept-list">
        {canCrm ? (
          <SharePointCategory title="Sales CRM" count={1}>
            <SharePointEnquiryPanel actorRole={actorRole} portalKind="crm" />
          </SharePointCategory>
        ) : null}
        {canIt ? (
          <SharePointCategory title="IT" count={1}>
            <SharePointEnquiryPanel actorRole={actorRole} portalKind="it" />
          </SharePointCategory>
        ) : null}
        {canForms ? (
          <SharePointCategory title="Forms" count={2}>
            <ApplicationFormSharePointPanel embeddedInAdminHub />
            <CustomerFeedbackSharePointPanel embeddedInAdminHub />
          </SharePointCategory>
        ) : null}
      </div>
    </section>
  );
}
